import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Patient.module.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
    FaUsers,
    FaUserPlus,
    FaFileImport,
    FaFileExport,
    FaProcedures,
    FaCalendarAlt,
    FaAmbulance,
    FaSearch,
    FaFilter,
    FaRedoAlt,
    FaEllipsisV,
    FaEye,
    FaEdit,
    FaMars,
    FaVenus,
    FaTimes,
    FaSave,
    FaUser,
    FaPhone,
    FaMapMarkerAlt,
    FaHeartbeat,
    FaExclamationTriangle,
    FaShieldAlt,
    FaUserMd,
    FaIdCard,
    FaTint,
    FaDna,
    FaRulerVertical,
    FaWeight,
    FaNotesMedical,
    FaCalendarCheck,
    FaSpinner,
} from "react-icons/fa";
import { useState, useEffect, useMemo, useCallback } from "react";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonStatsCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonIcon}></div>
        <div className={classes.skeletonCardInfo}>
            <div className={classes.skeletonText}></div>
            <div className={classes.skeletonText} style={{ width: "60%" }}></div>
            <div className={classes.skeletonText} style={{ width: "40%" }}></div>
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className={classes.skeletonRow}>
        <td>
            <div className={classes.skeletonPatientInfo}>
                <div className={classes.skeletonAvatar}></div>
                <div>
                    <div className={classes.skeletonText} style={{ width: "120px" }}></div>
                    <div className={classes.skeletonText} style={{ width: "80px" }}></div>
                </div>
            </div>
        </td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonBadge}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td>
            <div className={classes.skeletonActions}>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
            </div>
        </td>
    </tr>
);

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------
const safeValue = (val, fallback = "—") => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === "string") return val.trim() || fallback;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    if (typeof val === "object") {
        return val.name || val.title || val.label || fallback;
    }
    return fallback;
};

const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return dateString;
    }
};

const computeAgeFromDob = (dob) => {
    if (!dob) return null;
    try {
        const birth = new Date(dob);
        if (isNaN(birth.getTime())) return null;
        const now = new Date();
        let age = now.getFullYear() - birth.getFullYear();
        const m = now.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
        return age >= 0 ? age : null;
    } catch {
        return null;
    }
};

const getFullName = (patient) => {
    if (!patient) return "Unknown";
    if (patient.name) return patient.name;
    const first = patient.first_name || "";
    const middle = patient.middle_name || "";
    const last = patient.last_name || "";
    const combined = `${first} ${middle} ${last}`.replace(/\s+/g, " ").trim();
    return combined || "Unknown";
};

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

// ---------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------
const ViewPatientModal = ({ patient, open, onClose, onEdit }) => {
    if (!open || !patient) return null;

    const fullName = getFullName(patient);
    const dob = patient.date_of_birth;
    const age = patient.age || computeAgeFromDob(dob);
    const gender = patient.gender || "—";
    const isMale = gender.toLowerCase() === "male";
    const isFemale = gender.toLowerCase() === "female";

    const Info = ({ icon, label, value }) => (
        <div className={classes.modalInfoRow}>
            <div className={classes.modalInfoIcon}>{icon}</div>
            <div className={classes.modalInfoText}>
                <span className={classes.modalInfoLabel}>{label}</span>
                <span className={classes.modalInfoValue}>
                    {safeValue(value)}
                </span>
            </div>
        </div>
    );

    return (
        <div className={classes.modalOverlay} onClick={onClose}>
            <div
                className={classes.modalContent}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={classes.modalHeader}>
                    <div className={classes.modalHeaderLeft}>
                        <div className={classes.modalAvatar}>
                            {getInitials(fullName)}
                        </div>
                        <div>
                            <h2>{fullName}</h2>
                            <div className={classes.modalMetaRow}>
                                <span className={classes.modalMetaItem}>
                                    <FaIdCard /> {patient.patient_no || "—"}
                                </span>
                                <span className={classes.modalMetaItem}>
                                    {isMale ? (
                                        <FaMars className={classes.male} />
                                    ) : isFemale ? (
                                        <FaVenus className={classes.female} />
                                    ) : (
                                        <FaUser />
                                    )}
                                    {gender}
                                </span>
                                <span className={classes.modalMetaItem}>
                                    <FaCalendarAlt />{" "}
                                    {age ? `${age} yrs` : "—"}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={classes.modalCloseBtn}
                        onClick={onClose}
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={classes.modalBody}>
                    <div className={classes.modalGrid}>
                        <div className={classes.modalSection}>
                            <h4>Personal Information</h4>
                            <Info
                                icon={<FaUser />}
                                label="First Name"
                                value={patient.first_name}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Middle Name"
                                value={patient.middle_name}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Last Name"
                                value={patient.last_name}
                            />
                            <Info
                                icon={<FaCalendarAlt />}
                                label="Date of Birth"
                                value={formatDate(dob)}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Marital Status"
                                value={patient.marital_status}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Nationality"
                                value={patient.nationality}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Religion"
                                value={patient.religion}
                            />
                            <Info
                                icon={<FaUser />}
                                label="Occupation"
                                value={patient.occupation}
                            />
                        </div>

                        <div className={classes.modalSection}>
                            <h4>Contact Information</h4>
                            <Info
                                icon={<FaPhone />}
                                label="Phone"
                                value={patient.phone}
                            />
                            <Info
                                icon={<FaPhone />}
                                label="Email"
                                value={patient.email}
                            />
                            <Info
                                icon={<FaMapMarkerAlt />}
                                label="Address"
                                value={patient.address}
                            />
                            <Info
                                icon={<FaMapMarkerAlt />}
                                label="City"
                                value={patient.city}
                            />
                            <Info
                                icon={<FaMapMarkerAlt />}
                                label="State"
                                value={patient.state}
                            />
                            <Info
                                icon={<FaMapMarkerAlt />}
                                label="Country"
                                value={patient.country}
                            />
                            <Info
                                icon={<FaMapMarkerAlt />}
                                label="Postal Code"
                                value={patient.postal_code}
                            />
                        </div>

                        <div className={classes.modalSection}>
                            <h4>Medical Information</h4>
                            <Info
                                icon={<FaTint />}
                                label="Blood Group"
                                value={patient.blood_group}
                            />
                            <Info
                                icon={<FaDna />}
                                label="Genotype"
                                value={patient.genotype}
                            />
                            <Info
                                icon={<FaRulerVertical />}
                                label="Height (cm)"
                                value={patient.height}
                            />
                            <Info
                                icon={<FaWeight />}
                                label="Weight (kg)"
                                value={patient.weight}
                            />
                            <Info
                                icon={<FaHeartbeat />}
                                label="BMI"
                                value={patient.bmi}
                            />
                            <Info
                                icon={<FaNotesMedical />}
                                label="Past Medical History"
                                value={patient.past_med_history}
                            />
                            <Info
                                icon={<FaNotesMedical />}
                                label="Family Medical History"
                                value={patient.family_med_history}
                            />
                        </div>

                        <div className={classes.modalSection}>
                            <h4>Emergency & Insurance</h4>
                            <Info
                                icon={<FaExclamationTriangle />}
                                label="Emergency Contact"
                                value={patient.emergency_contact_name}
                            />
                            <Info
                                icon={<FaExclamationTriangle />}
                                label="Relationship"
                                value={patient.emergency_contact_relationship}
                            />
                            <Info
                                icon={<FaPhone />}
                                label="Emergency Phone"
                                value={patient.emergency_contact_phone}
                            />
                            <Info
                                icon={<FaShieldAlt />}
                                label="Insurance ID"
                                value={patient.insurance_id}
                            />
                        </div>

                        <div className={classes.modalSection}>
                            <h4>Primary Physician</h4>
                            <Info
                                icon={<FaUserMd />}
                                label="Department"
                                value={patient.department || patient.department_name}
                            />
                            <Info
                                icon={<FaUserMd />}
                                label="Doctor"
                                value={patient.doctor}
                            />
                            <Info
                                icon={<FaUserMd />}
                                label="Consultation Type"
                                value={patient.consultation_type}
                            />
                            <Info
                                icon={<FaUserMd />}
                                label="Referred By"
                                value={patient.referred_by}
                            />
                        </div>

                        <div className={classes.modalSection}>
                            <h4>Identification</h4>
                            <Info
                                icon={<FaIdCard />}
                                label="ID Type"
                                value={patient.identification_type}
                            />
                            <Info
                                icon={<FaIdCard />}
                                label="ID Number"
                                value={patient.identification_number}
                            />
                            <Info
                                icon={<FaCalendarCheck />}
                                label="Date Registered"
                                value={formatDate(patient.date_of_registration)}
                            />
                            <Info
                                icon={<FaCalendarCheck />}
                                label="Record Created"
                                value={formatDate(patient.created_at)}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.modalFooter}>
                    <button
                        type="button"
                        className={classes.modalCancelBtn}
                        onClick={onClose}
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        className={classes.modalPrimaryBtn}
                        onClick={onEdit}
                    >
                        <FaEdit /> Edit Patient
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------
// EDIT MODAL
// ---------------------------------------------------------
const EditPatientModal = ({ patient, open, onClose, onSaved }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (open && patient) {
            setForm({
                first_name: patient.first_name || "",
                middle_name: patient.middle_name || "",
                last_name: patient.last_name || "",
                date_of_birth: patient.date_of_birth
                    ? String(patient.date_of_birth).substring(0, 10)
                    : "",
                gender: patient.gender || "",
                phone: patient.phone || "",
                email: patient.email || "",
                address: patient.address || "",
                address_line_2: patient.address_line_2 || "",
                city: patient.city || "",
                state: patient.state || "",
                country: patient.country || "",
                postal_code: patient.postal_code || "",
                blood_group: patient.blood_group || "",
                genotype: patient.genotype || "",
                height: patient.height || "",
                weight: patient.weight || "",
                marital_status: patient.marital_status || "",
                nationality: patient.nationality || "",
                religion: patient.religion || "",
                occupation: patient.occupation || "",
                past_med_history: patient.past_med_history || "",
                family_med_history: patient.family_med_history || "",
                emergency_contact_name: patient.emergency_contact_name || "",
                emergency_contact_relationship:
                    patient.emergency_contact_relationship || "",
                emergency_contact_phone:
                    patient.emergency_contact_phone || "",
                insurance_id: patient.insurance_id || "",
            });
            setErr("");
        }
    }, [open, patient]);

    if (!open || !form) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setErr("");
        try {
            const companyId = parseInt(
                localStorage.getItem("companyId") || "1"
            );
            const payload = {
                company_id: companyId,
                first_name: form.first_name || null,
                middle_name: form.middle_name || null,
                last_name: form.last_name || null,
                date_of_birth: form.date_of_birth || null,
                gender: form.gender || null,
                phone: form.phone || null,
                email: form.email || null,
                address: form.address || null,
                address_line_2: form.address_line_2 || null,
                city: form.city || null,
                state: form.state || null,
                country: form.country || null,
                postal_code: form.postal_code || null,
                blood_group: form.blood_group || null,
                genotype: form.genotype || null,
                height: form.height || null,
                weight: form.weight || null,
                marital_status: form.marital_status || null,
                nationality: form.nationality || null,
                religion: form.religion || null,
                occupation: form.occupation || null,
                past_med_history: form.past_med_history || null,
                family_med_history: form.family_med_history || null,
                emergency_contact_name: form.emergency_contact_name || null,
                emergency_contact_relationship:
                    form.emergency_contact_relationship || null,
                emergency_contact_phone:
                    form.emergency_contact_phone || null,
                insurance_id: form.insurance_id
                    ? parseInt(form.insurance_id)
                    : null,
            };

            await axios.put(
                `${BASE_URL}/api/patient/${patient.id}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                            "authToken"
                        )}`,
                    },
                }
            );

            Swal.fire({
                icon: "success",
                title: "Patient Updated",
                text: "Changes have been saved.",
                timer: 1500,
                showConfirmButton: false,
            });

            if (onSaved) onSaved();
            onClose();
        } catch (error) {
            console.error("Error updating patient:", error);
            let msg = "Failed to update patient.";
            const data = error.response?.data;
            if (data?.message && typeof data.message === "object") {
                const firstKey = Object.keys(data.message)[0];
                const firstVal = data.message[firstKey];
                if (Array.isArray(firstVal) && firstVal[0]) msg = firstVal[0];
                else if (typeof firstVal === "string") msg = firstVal;
            } else if (typeof data?.message === "string") {
                msg = data.message;
            }
            setErr(msg);
        } finally {
            setSaving(false);
        }
    };

    const Field = ({ label, name, type = "text", options }) => (
        <div className={classes.modalField}>
            <label>{label}</label>
            {options ? (
                <select
                    name={name}
                    value={form[name] || ""}
                    onChange={handleChange}
                    className={classes.modalInput}
                >
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            ) : (
                <input
                    type={type}
                    name={name}
                    value={form[name] || ""}
                    onChange={handleChange}
                    className={classes.modalInput}
                />
            )}
        </div>
    );

    return (
        <div className={classes.modalOverlay} onClick={onClose}>
            <div
                className={classes.modalContent}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={classes.modalHeader}>
                    <div className={classes.modalHeaderLeft}>
                        <div className={classes.modalAvatar}>
                            {getInitials(getFullName(patient))}
                        </div>
                        <div>
                            <h2>Edit Patient</h2>
                            <p className={classes.modalSubtitle}>
                                {getFullName(patient)} •{" "}
                                {patient.patient_no || "—"}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={classes.modalCloseBtn}
                        onClick={onClose}
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={classes.modalBody}>
                    {err && <div className={classes.modalError}>{err}</div>}

                    <h4>Personal Information</h4>
                    <div className={classes.modalFormGrid}>
                        <Field label="First Name" name="first_name" />
                        <Field label="Middle Name" name="middle_name" />
                        <Field label="Last Name" name="last_name" />
                        <Field
                            label="Date of Birth"
                            name="date_of_birth"
                            type="date"
                        />
                        <Field
                            label="Gender"
                            name="gender"
                            options={[
                                { value: "", label: "Select gender" },
                                { value: "Male", label: "Male" },
                                { value: "Female", label: "Female" },
                                { value: "Other", label: "Other" },
                            ]}
                        />
                        <Field
                            label="Marital Status"
                            name="marital_status"
                            options={[
                                { value: "", label: "Select status" },
                                { value: "Single", label: "Single" },
                                { value: "Married", label: "Married" },
                                { value: "Divorced", label: "Divorced" },
                                { value: "Widowed", label: "Widowed" },
                            ]}
                        />
                        <Field label="Nationality" name="nationality" />
                        <Field label="Religion" name="religion" />
                        <Field label="Occupation" name="occupation" />
                    </div>

                    <h4>Contact Information</h4>
                    <div className={classes.modalFormGrid}>
                        <Field label="Phone" name="phone" />
                        <Field label="Email" name="email" type="email" />
                        <Field label="Address" name="address" />
                        <Field label="Address Line 2" name="address_line_2" />
                        <Field label="City" name="city" />
                        <Field label="State" name="state" />
                        <Field label="Country" name="country" />
                        <Field label="Postal Code" name="postal_code" />
                    </div>

                    <h4>Medical Information</h4>
                    <div className={classes.modalFormGrid}>
                        <Field
                            label="Blood Group"
                            name="blood_group"
                            options={[
                                { value: "", label: "Select" },
                                { value: "A+", label: "A+" },
                                { value: "A-", label: "A-" },
                                { value: "B+", label: "B+" },
                                { value: "B-", label: "B-" },
                                { value: "AB+", label: "AB+" },
                                { value: "AB-", label: "AB-" },
                                { value: "O+", label: "O+" },
                                { value: "O-", label: "O-" },
                            ]}
                        />
                        <Field
                            label="Genotype"
                            name="genotype"
                            options={[
                                { value: "", label: "Select" },
                                { value: "AA", label: "AA" },
                                { value: "AS", label: "AS" },
                                { value: "SS", label: "SS" },
                                { value: "AC", label: "AC" },
                            ]}
                        />
                        <Field label="Height (cm)" name="height" />
                        <Field label="Weight (kg)" name="weight" />
                        <Field
                            label="Past Medical History"
                            name="past_med_history"
                        />
                        <Field
                            label="Family Medical History"
                            name="family_med_history"
                        />
                    </div>

                    <h4>Emergency Contact</h4>
                    <div className={classes.modalFormGrid}>
                        <Field
                            label="Contact Name"
                            name="emergency_contact_name"
                        />
                        <Field
                            label="Relationship"
                            name="emergency_contact_relationship"
                        />
                        <Field
                            label="Phone"
                            name="emergency_contact_phone"
                        />
                        <Field label="Insurance ID" name="insurance_id" />
                    </div>
                </div>

                <div className={classes.modalFooter}>
                    <button
                        type="button"
                        className={classes.modalCancelBtn}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={classes.modalPrimaryBtn}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <FaSpinner className={classes.spin} /> Saving...
                            </>
                        ) : (
                            <>
                                <FaSave /> Save Changes
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------
function Patient() {
    const navigate = useNavigate();

    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [stats, setStats] = useState([
        {
            title: "Total Patients",
            value: "0",
            link: "View all patients",
            icon: <FaUsers />,
            color: "#2F80ED",
            bg: "#EDF5FF",
        },
        {
            title: "New Patients (This Month)",
            value: "0",
            link: "View new patients",
            icon: <FaUserPlus />,
            color: "#27AE60",
            bg: "#ECFFF5",
        },
        {
            title: "Admitted Patients",
            value: "0",
            link: "View admitted",
            icon: <FaProcedures />,
            color: "#9B51E0",
            bg: "#F7EEFF",
        },
        {
            title: "Outpatients (Today)",
            value: "0",
            link: "View outpatients",
            icon: <FaCalendarAlt />,
            color: "#F2994A",
            bg: "#FFF6EB",
        },
        {
            title: "Emergency (Today)",
            value: "0",
            link: "View emergency",
            icon: <FaAmbulance />,
            color: "#EB5757",
            bg: "#FFF0F0",
        },
    ]);

    const [search, setSearch] = useState("");
    const [department, setDepartment] = useState("");
    const [status, setStatus] = useState("");
    const [bloodGroup, setBloodGroup] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const [viewPatient, setViewPatient] = useState(null);
    const [editPatient, setEditPatient] = useState(null);
    const [viewOpen, setViewOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const rowsPerPage = 100;

    // Wrap fetchPatients in useCallback so it can be a dependency
    // of useEffect without triggering warnings.
    const fetchPatients = useCallback(async (page = 1) => {
        setLoading(true);
        setError("");
        try {
            const companyId = localStorage.getItem("companyId") || "1";

            const response = await axios.get(`${BASE_URL}/api/patient`, {
                params: {
                    company_id: parseInt(companyId),
                    page: page,
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });

            if (response.data && response.data.data) {
                const responseData = response.data.data;

                let patientsData = [];
                if (Array.isArray(responseData.data)) {
                    patientsData = responseData.data;
                } else if (Array.isArray(responseData)) {
                    patientsData = responseData;
                } else {
                    patientsData = [responseData];
                }

                setPatients(patientsData);

                // ----- STATS -----
                const total = patientsData.length || 0;
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                const newPatients = patientsData.filter((p) => {
                    if (p.created_at) {
                        const date = new Date(p.created_at);
                        return (
                            date.getMonth() === currentMonth &&
                            date.getFullYear() === currentYear
                        );
                    }
                    return false;
                });
                const admitted = patientsData.filter(
                    (p) =>
                        p.status?.toLowerCase() === "admitted" ||
                        p.status?.toLowerCase() === "inpatient"
                );
                const outpatients = patientsData.filter(
                    (p) =>
                        p.status?.toLowerCase() === "active" ||
                        p.status?.toLowerCase() === "outpatient"
                );
                const emergency = patientsData.filter(
                    (p) =>
                        p.department?.toLowerCase() === "emergency" ||
                        p.consultation_type?.toLowerCase() === "emergency"
                );

                setStats([
                    {
                        title: "Total Patients",
                        value: total.toString(),
                        link: "View all patients",
                        icon: <FaUsers />,
                        color: "#2F80ED",
                        bg: "#EDF5FF",
                    },
                    {
                        title: "New Patients (This Month)",
                        value: newPatients.length.toString(),
                        link: "View new patients",
                        icon: <FaUserPlus />,
                        color: "#27AE60",
                        bg: "#ECFFF5",
                    },
                    {
                        title: "Admitted Patients",
                        value: admitted.length.toString(),
                        link: "View admitted",
                        icon: <FaProcedures />,
                        color: "#9B51E0",
                        bg: "#F7EEFF",
                    },
                    {
                        title: "Outpatients (Today)",
                        value: outpatients.length.toString(),
                        link: "View outpatients",
                        icon: <FaCalendarAlt />,
                        color: "#F2994A",
                        bg: "#FFF6EB",
                    },
                    {
                        title: "Emergency (Today)",
                        value: emergency.length.toString(),
                        link: "View emergency",
                        icon: <FaAmbulance />,
                        color: "#EB5757",
                        bg: "#FFF0F0",
                    },
                ]);
            } else {
                setPatients([]);
            }
        } catch (err) {
            console.error("Error fetching patients:", err);
            let errorMsg = "Failed to load patients. Please try again.";
            if (err.response) {
                if (err.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (err.response.data?.message) {
                    const message = err.response.data.message;
                    if (typeof message === "object") {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey && Array.isArray(message[firstKey])) {
                            errorMsg = message[firstKey][0];
                        } else {
                            errorMsg = JSON.stringify(message);
                        }
                    } else if (typeof message === "string") {
                        errorMsg = message;
                    }
                }
            } else if (err.request) {
                errorMsg =
                    "Unable to connect to server. Please check your internet connection.";
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load patients on mount and when the page changes.
    useEffect(() => {
        fetchPatients(currentPage);
    }, [fetchPatients, currentPage]);

    // Avatar helper
    const getAvatar = (patient) => {
        if (patient.avatar) return patient.avatar;
        const gender =
            patient.gender?.toLowerCase() === "female" ? "women" : "men";
        const id = patient.id || Math.floor(Math.random() * 100);
        return `https://randomuser.me/api/portraits/${gender}/${
            (id % 70) + 1
        }.jpg`;
    };

    const departments = useMemo(() => {
        const depts = new Set();
        patients.forEach((p) => {
            if (p.department) depts.add(p.department);
            if (p.department_name) depts.add(p.department_name);
        });
        return Array.from(depts);
    }, [patients]);

    const statuses = useMemo(() => {
        const stat = new Set();
        patients.forEach((p) => {
            if (p.status) stat.add(p.status);
        });
        return Array.from(stat);
    }, [patients]);

    const bloodGroups = useMemo(() => {
        const bg = new Set();
        patients.forEach((p) => {
            if (p.blood_group) bg.add(p.blood_group);
        });
        return Array.from(bg);
    }, [patients]);

    const filteredPatients = useMemo(() => {
        return patients.filter((patient) => {
            const keyword = search.toLowerCase();
            const fullName = getFullName(patient).toLowerCase();

            const searchMatch =
                patient.first_name?.toLowerCase().includes(keyword) ||
                patient.last_name?.toLowerCase().includes(keyword) ||
                patient.patient_no?.toLowerCase().includes(keyword) ||
                patient.phone?.toLowerCase().includes(keyword) ||
                patient.email?.toLowerCase().includes(keyword) ||
                fullName.includes(keyword);

            const departmentMatch =
                department === "" ||
                patient.department === department ||
                patient.department_name === department;

            const statusMatch = status === "" || patient.status === status;

            const bloodMatch =
                bloodGroup === "" || patient.blood_group === bloodGroup;

            return (
                searchMatch && departmentMatch && statusMatch && bloodMatch
            );
        });
    }, [patients, search, department, status, bloodGroup]);

    const totalPages = Math.ceil(filteredPatients.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentPatients = filteredPatients.slice(indexOfFirst, indexOfLast);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleView = (patient) => {
        if (!patient) return;
        setViewPatient(patient);
        setViewOpen(true);
    };

    const handleEdit = (patient) => {
        if (!patient) return;
        setEditPatient(patient);
        setEditOpen(true);
    };

    const handleViewToEdit = () => {
        const p = viewPatient;
        setViewOpen(false);
        if (p) {
            setEditPatient(p);
            setEditOpen(true);
        }
    };

    const handleMore = (patient) => {
        Swal.fire({
            icon: "info",
            title: getFullName(patient),
            text: `UHID: ${patient.patient_no || "N/A"} • Status: ${
                patient.status || "Active"
            }`,
            showCancelButton: true,
            confirmButtonText: "View",
            cancelButtonText: "Close",
            confirmButtonColor: "#2F80ED",
        }).then((res) => {
            if (res.isConfirmed) handleView(patient);
        });
    };

    const resetFilters = () => {
        setSearch("");
        setDepartment("");
        setStatus("");
        setBloodGroup("");
        setCurrentPage(1);
    };

    const renderSkeletonRows = () =>
        Array(8)
            .fill(0)
            .map((_, index) => <SkeletonRow key={`skeleton-${index}`} />);

    const renderSkeletonStats = () =>
        Array(5)
            .fill(0)
            .map((_, index) => (
                <SkeletonStatsCard key={`skeleton-stat-${index}`} />
            ));

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    {/* TOP */}
                    <div className={classes.top}>
                        <div>
                            <h2>Patients</h2>
                            <div className={classes.breadcrumb}>
                                Dashboard
                                <span>&gt;</span>
                                Patients
                            </div>
                        </div>
                        <div className={classes.actions}>
                            <button className={classes.outline}>
                                <FaFileImport /> Import Patients
                            </button>
                            <button className={classes.outline}>
                                <FaFileExport /> Export
                            </button>
                            <button
                                className={classes.primary}
                                onClick={() => navigate("/addPatientPage")}
                            >
                                + Add New Patient
                            </button>
                        </div>
                    </div>

                    {/* STATS */}
                    <div className={classes.cards}>
                        {loading
                            ? renderSkeletonStats()
                            : stats.map((item, index) => (
                                  <div className={classes.card} key={index}>
                                      <div
                                          className={classes.icon}
                                          style={{
                                              background: item.bg,
                                              color: item.color,
                                          }}
                                      >
                                          {item.icon}
                                      </div>
                                      <div className={classes.cardInfo}>
                                          <p>{item.title}</p>
                                          <h2>{item.value}</h2>
                                          <button>{item.link}</button>
                                      </div>
                                  </div>
                              ))}
                    </div>

                    {/* SEARCH / FILTERS */}
                    <div className={classes.searchContainer}>
                        <div className={classes.searchBox}>
                            <FaSearch className={classes.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by Name, UHID, Phone, Email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Departments</option>
                            {departments.map((dept) => (
                                <option key={dept} value={dept}>
                                    {dept}
                                </option>
                            ))}
                        </select>

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Status</option>
                            {statuses.map((stat) => (
                                <option key={stat} value={stat}>
                                    {stat}
                                </option>
                            ))}
                        </select>

                        <select
                            value={bloodGroup}
                            onChange={(e) => setBloodGroup(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Blood Groups</option>
                            {bloodGroups.map((bg) => (
                                <option key={bg} value={bg}>
                                    {bg}
                                </option>
                            ))}
                        </select>

                        <button
                            className={classes.filterBtn}
                            disabled={loading}
                        >
                            <FaFilter /> More Filters
                        </button>

                        <button
                            className={classes.resetBtn}
                            onClick={resetFilters}
                            disabled={loading}
                        >
                            <FaRedoAlt /> Reset
                        </button>
                    </div>

                    {/* ERROR */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button
                                onClick={() => fetchPatients(currentPage)}
                                className={classes.retryBtn}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {/* TABLE */}
                    <div
                        className={`${classes.tableContainer} ${classes.scrollableTable}`}
                    >
                        {loading ? (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>UHID</th>
                                        <th>Gender</th>
                                        <th>Age</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th>Last Visit</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>{renderSkeletonRows()}</tbody>
                            </table>
                        ) : (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Patient</th>
                                        <th>UHID</th>
                                        <th>Gender</th>
                                        <th>Age</th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Department</th>
                                        <th>Status</th>
                                        <th>Last Visit</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPatients.length > 0 ? (
                                        currentPatients.map((patient) => {
                                            const fullName =
                                                getFullName(patient);
                                            const avatar = getAvatar(patient);
                                            const dob = patient.date_of_birth;
                                            const age =
                                                patient.age ||
                                                computeAgeFromDob(dob) ||
                                                "N/A";
                                            const phone =
                                                patient.phone || "N/A";
                                            const email =
                                                patient.email || "N/A";
                                            const dept =
                                                patient.department ||
                                                patient.department_name ||
                                                "N/A";
                                            const stat =
                                                patient.status || "Active";
                                            const lastVisit = patient.updated_at
                                                ? formatDate(
                                                      patient.updated_at
                                                  )
                                                : "N/A";

                                            return (
                                                <tr key={patient.id}>
                                                    <td>
                                                        <div
                                                            className={
                                                                classes.patientInfo
                                                            }
                                                        >
                                                            <img
                                                                src={avatar}
                                                                alt={fullName}
                                                                className={
                                                                    classes.avatar
                                                                }
                                                                onError={(
                                                                    e
                                                                ) => {
                                                                    e.target.style.display =
                                                                        "none";
                                                                    e.target.nextSibling.style.display =
                                                                        "flex";
                                                                }}
                                                            />
                                                            <div
                                                                className={
                                                                    classes.avatarFallback
                                                                }
                                                                style={{
                                                                    display:
                                                                        "none",
                                                                }}
                                                            >
                                                                {getInitials(
                                                                    fullName
                                                                )}
                                                            </div>
                                                            <div>
                                                                <h4>
                                                                    {fullName}
                                                                </h4>
                                                                <span>
                                                                    DOB :{" "}
                                                                    {formatDate(
                                                                        dob
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {patient.patient_no ||
                                                            "N/A"}
                                                    </td>
                                                    <td>
                                                        <div
                                                            className={
                                                                classes.gender
                                                            }
                                                        >
                                                            {patient.gender?.toLowerCase() ===
                                                            "male" ? (
                                                                <>
                                                                    <FaMars
                                                                        className={
                                                                            classes.male
                                                                        }
                                                                    />{" "}
                                                                    Male
                                                                </>
                                                            ) : patient.gender?.toLowerCase() ===
                                                              "female" ? (
                                                                <>
                                                                    <FaVenus
                                                                        className={
                                                                            classes.female
                                                                        }
                                                                    />{" "}
                                                                    Female
                                                                </>
                                                            ) : (
                                                                patient.gender ||
                                                                "N/A"
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{age}</td>
                                                    <td>{phone}</td>
                                                    <td>{email}</td>
                                                    <td>{dept}</td>
                                                    <td>
                                                        <span
                                                            className={`${
                                                                classes.badge
                                                            } ${
                                                                stat.toLowerCase() ===
                                                                    "active" ||
                                                                stat.toLowerCase() ===
                                                                    "outpatient"
                                                                    ? classes.active
                                                                    : stat.toLowerCase() ===
                                                                          "admitted" ||
                                                                      stat.toLowerCase() ===
                                                                          "inpatient"
                                                                    ? classes.admitted
                                                                    : classes.discharged
                                                            }`}
                                                        >
                                                            {stat}
                                                        </span>
                                                    </td>
                                                    <td>{lastVisit}</td>
                                                    <td>
                                                        <div
                                                            className={
                                                                classes.tableActions
                                                            }
                                                        >
                                                            <button
                                                                onClick={() =>
                                                                    handleView(
                                                                        patient
                                                                    )
                                                                }
                                                                className={
                                                                    classes.viewBtn
                                                                }
                                                                title="View Patient"
                                                                type="button"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    handleEdit(
                                                                        patient
                                                                    )
                                                                }
                                                                className={
                                                                    classes.editBtn
                                                                }
                                                                title="Edit Patient"
                                                                type="button"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    handleMore(
                                                                        patient
                                                                    )
                                                                }
                                                                className={
                                                                    classes.moreBtn
                                                                }
                                                                title="More Options"
                                                                type="button"
                                                            >
                                                                <FaEllipsisV />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan="10"
                                                style={{
                                                    textAlign: "center",
                                                    padding: "40px",
                                                }}
                                            >
                                                No Patients Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* PAGINATION */}
                    {!loading && filteredPatients.length > 0 && (
                        <div className={classes.pagination}>
                            <p>
                                Showing{" "}
                                <strong>
                                    {filteredPatients.length > 0
                                        ? indexOfFirst + 1
                                        : 0}
                                    -
                                    {Math.min(
                                        indexOfLast,
                                        filteredPatients.length
                                    )}
                                </strong>{" "}
                                of{" "}
                                <strong>{filteredPatients.length}</strong>{" "}
                                patients
                            </p>
                            <div className={classes.pages}>
                                <button
                                    onClick={() =>
                                        goToPage(currentPage - 1)
                                    }
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {Array.from(
                                    { length: totalPages },
                                    (_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => goToPage(i + 1)}
                                            className={
                                                currentPage === i + 1
                                                    ? classes.activePage
                                                    : ""
                                            }
                                        >
                                            {i + 1}
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={() =>
                                        goToPage(currentPage + 1)
                                    }
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* MODALS */}
            <ViewPatientModal
                patient={viewPatient}
                open={viewOpen}
                onClose={() => setViewOpen(false)}
                onEdit={handleViewToEdit}
            />

            <EditPatientModal
                patient={editPatient}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={() => fetchPatients(currentPage)}
            />
        </div>
    );
}

export default Patient;